import React, { FC, useEffect, useState } from 'react'
import router from 'next/router'
import useTranslation from 'next-translate/useTranslation'
import { Nav, Sidebar, Sidenav, Navbar } from 'rsuite'
import LegacyAngleLeftIcon from '@rsuite/icons/legacy/AngleLeft'
import LegacyAngleRightIcon from '@rsuite/icons/legacy/AngleRight'
import { IconProps } from '@rsuite/icons/lib/Icon'

import { useAuth } from '../../services/useAuth'
import { debounce } from '../../utils/event-utils'
import { ProfilePageTabs } from '../../utils/types/Profile'
import useContactModal from '../Modals/hooks/useContactModal'
import { PRIMARY_DARKER, DARK_GREY_COLOR } from '../../utils/constants'

import styles from '../../styles/SideNavBar.module.less'

export interface NavItemProps<T extends string> {
  label: string
  eventKey: T
  route?: string
  isContactModel?: boolean
  icon?: FC<IconProps>
  disabled?: boolean
  classes?: string
  testId?: string
}

export interface SideNavBarProps<T extends string> {
  activeTab: string
  handleSelect: (key: ProfilePageTabs) => void
  navItems: NavItemProps<T>[]
  title?: string
  showUser?: boolean
}

// Widths of rsuite SideNav
const sideBarWidthOpen = 250
const sideBarWidthClosed = 56

const SideNavBar = <T extends string>({
  activeTab,
  handleSelect,
  navItems,
  title = '',
  showUser = true,
}: SideNavBarProps<T>) => {
  const { t } = useTranslation('profile')
  const [expand, setExpand] = useState(true)
  const [canExpand, setCanExpand] = useState(true)
  const { user } = useAuth()
  const openContactModal = useContactModal('footer_modal', 'main_footer_contact_modal')

  const sidebarWidth: number = (canExpand && expand) ? sideBarWidthOpen : sideBarWidthClosed

  const handleSidebar = () => {
    setExpand(!expand)
  }
  const handleAllowResize = () => {
    // rsuite extra small-small threshold 480px
    const widthThreshold = getComputedStyle(document.documentElement).getPropertyValue('--screen-phone')
    const mq = window.matchMedia(`(min-width: ${widthThreshold})`)
    if (mq.matches) {
      setCanExpand(true)
    } else {
      setCanExpand(false)
    }
  }
  const debouncedHandleResize = debounce(handleAllowResize, 100)

  const onItemSelect = (navItem: NavItemProps<T>) => {
    if (navItem.isContactModel) {
      openContactModal()
    } else {
      router.push(`/${navItem.route}`, undefined, { shallow: true })
    }
  }

  const getColor = (currentActiveTab: string, eventKey: string) => {
    if (currentActiveTab === eventKey) {
      return PRIMARY_DARKER
    }

    return DARK_GREY_COLOR
  }

  useEffect(() => {
    handleAllowResize()
    window.addEventListener('resize', debouncedHandleResize)
    return () => {
      window.removeEventListener('resize', debouncedHandleResize)
    }
  }, [])

  return (
    <Sidebar
      collapsible
      width={sidebarWidth}
      className={styles['custom-sidebar']}
    >
      <Sidenav
        appearance="subtle"
        expanded={expand}
      >
        <Sidenav.Body>
          {expand && canExpand && title && <h2 className={styles['profile-menu-title']}>{t(title)}</h2>}
          {expand && canExpand && showUser && <p className={styles['profile-menu-name']}>{`${user?.firstname} ${user?.lastname}`}</p>}
          <Nav
            activeKey={activeTab}
            onSelect={(key) => handleSelect(key as ProfilePageTabs)}
          >
            {navItems.map((navItem) => (
              <Nav.Item
                data-testid={navItem.testId}
                key={navItem.label}
                eventKey={navItem.eventKey}
                icon={navItem.icon
                  ? (
                    <navItem.icon
                      color={getColor(activeTab, navItem.eventKey)}
                    />
                  )
                  : undefined}
                onSelect={() => onItemSelect(navItem)}
                disabled={navItem.disabled}
                className={`${navItem.classes} ${navItem.route === t('url:my-products') ? styles['heart-icon'] : styles[navItem.classes || '']} ${styles['icon-text-gap']}`}
              >
                {t(navItem.label)}
              </Nav.Item>
            ))}
          </Nav>
        </Sidenav.Body>
      </Sidenav>
      <Navbar
        appearance="subtle"
        style={{ width: sidebarWidth }}
      >
        <Nav pullRight>
          <Nav.Item
            disabled={!canExpand}
            onClick={handleSidebar}
            className={styles['supplier-products-sidebar-toggle']}
          >
            {expand ? <LegacyAngleLeftIcon /> : <LegacyAngleRightIcon />}
          </Nav.Item>
        </Nav>
      </Navbar>
    </Sidebar>
  )
}

export default SideNavBar
